import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Paper,
  Divider,
  Typography,
  Autocomplete,
  TextField,
  Chip,
  Stack,
  Tooltip,
  CircularProgress,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import PaymentsIcon from '@mui/icons-material/Payments';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import * as actions from '../../redux/actions';
import api from '../../utils/functions/api';
import { showAlert, confirmAsync } from '../../utils/functions/globalDialog';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';
import EmployeeAvatar from '../../components/EmployeeAvatar';
import { APPROVER_SLOT_COUNT, getApproverSlots } from '../../utils/functions/approverAccess';

const PAGE_SIZE = 50;

// รายการ "ประเภทการอนุมัติ" ที่ตั้งค่าได้ในหน้านี้ — เพิ่ม object ใหม่ในนี้เพื่อรองรับการอนุมัติ
// ประเภทอื่นในอนาคต (เช่น ผู้อนุมัติค่าแรง ผู้อนุมัติงบประมาณ ฯลฯ) โดยไม่ต้องแก้โครง UI เลย — แค่ตั้ง
// key ใหม่ไม่ซ้ำ ไปเก็บที่ Information.approver_settings (array, match ด้วย key) ทาง backend ไม่ต้อง
// แก้ schema เพิ่มอีกแล้ว (ดู backend/models/Information.js)
// allowThreeStep: false = ปุ่ม "3 Step" ถูก disable ไว้ บังคับเป็น 1 Step อย่างเดียว (ใช้กับ category
// ที่ตั้งใจให้เป็น "ใครก็ได้ในกลุ่ม" ไม่ต้องอนุมัติตามลำดับ เช่นผู้อนุมัติการจ่ายเงิน)
//
// supportsMenuAccessMode + menuHref: เฉพาะ category ที่ต้องการ gate ปุ่ม "จัดการ" (อนุมัติ/ปฏิเสธ)
// ในตารางที่ใช้ร่วมกันทั้งโมดูล HUMEN (ไม่ใช่หน้าแยกเฉพาะแบบ payment/expense_reviewer) — เปิดให้เลือก
// assign_mode ได้ 2 แบบ: 'SPECIFIC' (เลือกคนเฉพาะ เหมือน payment/expense_reviewer) หรือ
// 'MENU_ACCESS' (ไม่ต้องเลือกคน ใครก็ตามที่ role มีสิทธิ์เห็นเมนู menuHref ถือว่ามีสิทธิ์ — ดู
// frontend/src/utils/functions/approverAccess.js) ยังไม่เคยตั้งค่าเลย = ทุกคนเห็นปุ่มเหมือนเดิม
// (ไม่ทำให้ workflow ปัจจุบันพังตอน deploy จนกว่า admin จะมาตั้งค่าเอง)
const APPROVAL_CATEGORIES = [
  {
    key: 'payment',
    label: 'ผู้อนุมัติการจ่ายเงิน',
    icon: PaymentsIcon,
    description: 'กำหนดพนักงานที่มีสิทธิ์อนุมัติการจ่ายเงิน',
    allowThreeStep: false,
    roleLabel: 'ผู้อนุมัติ',
  },
  {
    key: 'expense_reviewer',
    label: 'ผู้ตรวจสอบรายการเบิกเงิน',
    icon: FactCheckOutlinedIcon,
    description: 'กำหนดพนักงานที่มีสิทธิ์ตรวจสอบรายการเบิกเงิน',
    allowThreeStep: false,
    roleLabel: 'ผู้ตรวจสอบ',
  },
  {
    key: 'ot_approver',
    label: 'อนุมัติคำขอโอที',
    icon: AccessTimeOutlinedIcon,
    description: 'กำหนดว่าใครเห็นปุ่มอนุมัติ/ปฏิเสธคำขอโอทีในหน้า "คำขอโอที"',
    allowThreeStep: false,
    roleLabel: 'ผู้มีสิทธิ์อนุมัติ',
    supportsMenuAccessMode: true,
    menuHref: '/humen/ot-request',
  },
  {
    key: 'leave_approver',
    label: 'อนุมัติคำขอลา',
    icon: EventBusyOutlinedIcon,
    description: 'กำหนดว่าใครเห็นปุ่มอนุมัติ/ปฏิเสธคำขอลาในหน้า "คำขอลา"',
    allowThreeStep: false,
    roleLabel: 'ผู้มีสิทธิ์อนุมัติ',
    supportsMenuAccessMode: true,
    menuHref: '/humen/leave-request',
  },
  {
    key: 'field_allowance_approver',
    label: 'อนุมัติคำขอค่าออกสนาม',
    icon: LocationOnOutlinedIcon,
    description: 'กำหนดว่าใครเห็นปุ่มอนุมัติ/ปฏิเสธคำขอค่าออกสนามในหน้า "คำขอค่าออกสนาม"',
    allowThreeStep: false,
    roleLabel: 'ผู้มีสิทธิ์อนุมัติ',
    supportsMenuAccessMode: true,
    menuHref: '/humen/field-allowance-request',
  },
];

// เลือกพนักงานทีละ 1 คน — ใช้ทำช่องผู้อนุมัติคนที่ 1..APPROVER_SLOT_COUNT เหมือน HrExpenseApproverSettings.js
function EmployeeAutocomplete({ value, onChange, color, placeholder }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const searchTimer = useRef(null);
  const hasMore = rows.length < total;

  const load = useCallback(async (p, q, reset) => {
    setLoading(true);
    try {
      const { data, status } = await api.get(
        `${process.env.REACT_APP_API_URL}/employee?name=${q}&size=${PAGE_SIZE}&page=${p}`,
      );
      if (status === 200) {
        setTotal(data?.total || 0);
        setRows((prev) => (reset ? (data?.rows || []) : [...prev, ...(data?.rows || [])]));
        setPage(p);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // โหลดรายชื่อตอนเปิด dropdown ครั้งแรก — ไม่โหลดตอน mount เพราะหน้านี้มีหลายช่อง (ยิง API พร้อมกันทุกช่อง)
  const handleOpen = () => {
    if (!rows.length && !loading) load(1, search, true);
  };

  const handleInputChange = (_, val, reason) => {
    if (reason !== 'input') return;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => load(1, val, true), 300);
  };

  const handleScroll = (e) => {
    const el = e.currentTarget;
    if (!hasMore || loading) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) {
      load(page + 1, search, false);
    }
  };

  return (
    <Autocomplete
      value={value}
      options={rows}
      getOptionLabel={(o) => (typeof o === 'string' ? o : `${formatEmployeeThaiName(o)} (${o?.employee_id || ''})`)}
      isOptionEqualToValue={(o, v) => o?._id === v?._id}
      onChange={(_, v) => onChange(v)}
      filterOptions={(x) => x}
      onInputChange={handleInputChange}
      onOpen={handleOpen}
      loading={loading}
      ListboxProps={{ onScroll: handleScroll, style: { maxHeight: 280 } }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={placeholder}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': { borderColor: color },
              '&.Mui-focused fieldset': { borderColor: color },
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={16} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option._id}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmployeeAvatar employee={option} size={28} />
            <Box>
              <Typography variant="body2">{formatEmployeeThaiName(option)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option?.employee_id} · {option?.department?.name || '-'}
              </Typography>
            </Box>
          </Box>
        </li>
      )}
    />
  );
}

export default function ApproverSettings({ title }) {
  const dispatch = useDispatch();
  const info = useSelector((state) => state.info);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedKey, setSelectedKey] = useState(APPROVAL_CATEGORIES[0].key);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);
  const [mode, setMode] = useState('ONE_STEP');
  const [assignMode, setAssignMode] = useState('SPECIFIC');
  const [approvers, setApprovers] = useState(() => Array(APPROVER_SLOT_COUNT).fill(null));
  const [saving, setSaving] = useState(false);

  const selectedCategory = APPROVAL_CATEGORIES.find((c) => c.key === selectedKey);

  useEffect(() => {
    dispatch(actions.getInformation({}));
  }, []);

  // sync จาก info.approver_settings ทุกครั้งที่สลับ category หรือ info โหลดใหม่ — category ที่ยัง
  // ไม่มี config เก็บไว้เลย (ตั้งค่าครั้งแรก) ถือเป็นค่าเปล่าทั้งหมด
  useEffect(() => {
    if (!info?._id || !selectedCategory) return;
    const cfg = (info?.approver_settings || []).find((c) => c.key === selectedCategory.key);
    setMode(selectedCategory.allowThreeStep ? (cfg?.mode || 'ONE_STEP') : 'ONE_STEP');
    setAssignMode(cfg?.assign_mode || 'SPECIFIC');
    setApprovers(getApproverSlots(cfg).map((v) => v || null));
  }, [info?._id, selectedKey]);

  const handleSelectCategory = (key) => {
    setSelectedKey(key);
    if (isMobile) setMobileShowDetail(true);
  };

  const handleBackToList = () => setMobileShowDetail(false);

  const handleSave = async () => {
    if (!selectedCategory) return;
    const confirm = await confirmAsync(`บันทึกการตั้งค่า "${selectedCategory.label}"?`);
    if (!confirm) return;
    setSaving(true);
    try {
      const nextConfig = {
        key: selectedCategory.key,
        mode,
        assign_mode: selectedCategory.supportsMenuAccessMode ? assignMode : 'SPECIFIC',
        // THREE_STEP ใช้แค่ 3 ช่องแรก — ช่องที่ซ่อนอยู่ล้างเป็น null กันค้างสิทธิ์โดยมองไม่เห็น
        ...Object.fromEntries(
          approvers.map((a, i) => [
            `approver_${i + 1}`,
            mode === 'THREE_STEP' && i >= 3 ? null : a?._id || null,
          ]),
        ),
      };
      const nextSettings = [
        ...(info?.approver_settings || []).filter((c) => c.key !== selectedCategory.key),
        nextConfig,
      ];
      await dispatch(
        actions.editOneInformation(info?._id, { approver_settings: nextSettings }),
      );
      await dispatch(actions.getInformation({}));
      showAlert('บันทึกสำเร็จ');
    } catch (err) {
      console.error(err);
      showAlert('เกิดข้อผิดพลาด', { status: err?.response?.status });
    } finally {
      setSaving(false);
    }
  };

  const renderCategoryItem = (cat) => {
    const isSelected = cat.key === selectedKey;
    const Icon = cat.icon;
    return (
      <Box
        key={cat.key}
        onClick={() => handleSelectCategory(cat.key)}
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderLeft: '3px solid',
          borderLeftColor: isSelected ? 'primary.main' : 'transparent',
          bgcolor: isSelected ? 'action.selected' : 'transparent',
          transition: 'all 0.15s ease',
          px: 2,
          py: 1.5,
          '&:hover': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
        }}
      >
        <Icon fontSize="small" color={isSelected ? 'primary' : 'inherit'} />
        <Typography variant="body2" sx={{ fontWeight: isSelected ? 700 : 500 }}>
          {cat.label}
        </Typography>
      </Box>
    );
  };

  const renderLeftPanel = () => (
    <Box sx={{
      width: { md: 280 },
      minWidth: { md: 280 },
      borderRight: { md: '1px solid' },
      borderColor: 'divider',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 2, py: 1.5 }}>
        <HowToRegOutlinedIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
          ประเภทการอนุมัติ ({APPROVAL_CATEGORIES.length})
        </Typography>
      </Box>
      <Divider />
      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        {APPROVAL_CATEGORIES.map(renderCategoryItem)}
      </Box>
    </Box>
  );

  const renderRightPanel = () => {
    if (!selectedCategory) return null;
    const Icon = selectedCategory.icon;
    const isOneStep = mode === 'ONE_STEP';

    const roleLabel = selectedCategory.roleLabel || 'ผู้อนุมัติ';
    const STEP_ORDINAL = ['1st', '2nd', '3rd'];
    const slotColors = [
      theme.palette.warning.main,
      theme.palette.info.main,
      theme.palette.success.main,
      theme.palette.secondary.main,
      theme.palette.primary.main,
    ];
    // 1 Step = ครบทุกช่อง (ใครก็ได้), 3 Steps = 3 ช่องแรกตามลำดับขั้น
    const steps = approvers.slice(0, isOneStep ? APPROVER_SLOT_COUNT : 3).map((value, i) => ({
      label: isOneStep ? `${roleLabel}คนที่ ${i + 1}` : `${roleLabel}ขั้นที่ ${i + 1} (${STEP_ORDINAL[i]})`,
      color: slotColors[i % slotColors.length],
      value,
      onChange: (v) => setApprovers((prev) => prev.map((p, j) => (j === i ? v : p))),
    }));
    const filledCount = steps.filter((st) => st.value).length;

    return (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {isMobile && (
          <Box sx={{ px: 2, pt: 1.5, pb: 0 }}>
            <Button size="small" startIcon={<ArrowBackIcon />} onClick={handleBackToList} sx={{ color: 'text.secondary' }}>
              ประเภทการอนุมัติ
            </Button>
          </Box>
        )}

        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{selectedCategory.label}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {selectedCategory.description}
          </Typography>
        </Box>
        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, sm: 2.5 } }}>
          {/* Mode selector: 1-step vs 3-step */}
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 1.5,
            }}
          >
            <Typography variant="subtitle2" sx={{ minWidth: 140 }}>
              รูปแบบการอนุมัติ:
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant={isOneStep ? 'contained' : 'outlined'}
                size="small"
                color="primary"
                onClick={() => setMode('ONE_STEP')}
              >
                1 Step
              </Button>
              <Tooltip title={selectedCategory.allowThreeStep ? '' : 'การอนุมัติประเภทนี้ไม่รองรับการอนุมัติตามลำดับขั้น'}>
                <span>
                  <Button
                    variant={!isOneStep ? 'contained' : 'outlined'}
                    size="small"
                    color="primary"
                    disabled={!selectedCategory.allowThreeStep}
                    onClick={() => setMode('THREE_STEP')}
                  >
                    3 Steps
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Box>

          {/* Assign mode selector: เลือกคนเฉพาะ vs เฉพาะคนที่เข้าถึงเมนูนี้ — เฉพาะ category ที่
              supportsMenuAccessMode (ดู comment บน APPROVAL_CATEGORIES) */}
          {selectedCategory.supportsMenuAccessMode && (
            <Box
              sx={{
                mb: 3,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 1.5,
              }}
            >
              <Typography variant="subtitle2" sx={{ minWidth: 140 }}>
                กำหนดสิทธิ์โดย:
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant={assignMode === 'SPECIFIC' ? 'contained' : 'outlined'}
                  size="small"
                  color="primary"
                  onClick={() => setAssignMode('SPECIFIC')}
                >
                  เลือกคนเฉพาะ
                </Button>
                <Button
                  variant={assignMode === 'MENU_ACCESS' ? 'contained' : 'outlined'}
                  size="small"
                  color="primary"
                  onClick={() => setAssignMode('MENU_ACCESS')}
                >
                  เฉพาะคนที่เข้าถึงเมนูนี้
                </Button>
              </Stack>
            </Box>
          )}

          {selectedCategory.supportsMenuAccessMode && assignMode === 'MENU_ACCESS' ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.info.main, 0.08),
              }}
            >
              <InfoOutlinedIcon fontSize="small" sx={{ color: theme.palette.info.main, mt: 0.25 }} />
              <Typography variant="body2" color="text.secondary">
                ไม่ต้องเลือกพนักงาน — ใครก็ตามที่สิทธิ์ (role) ของตัวเองเข้าถึงเมนูนี้ได้
                จะเห็นปุ่มในคอลัมน์ "จัดการ" ของหน้านี้โดยอัตโนมัติ
              </Typography>
            </Box>
          ) : (
            <>
              {/* Flow diagram hint */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 3,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  flexWrap: 'wrap',
                }}
              >
                {isOneStep ? (
                  <Chip
                    label={`${roleLabel}คนใดก็ได้ในรายการ · เลือกแล้ว ${filledCount}/${APPROVER_SLOT_COUNT} คน`}
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.warning.main, 0.15),
                      color: theme.palette.warning.main,
                      fontWeight: 600,
                    }}
                  />
                ) : (
                  steps.map((step, idx) => (
                    <React.Fragment key={idx}>
                      <Chip
                        label={`Step ${idx + 1}`}
                        size="small"
                        sx={{
                          bgcolor: alpha(step.color, 0.15),
                          color: step.color,
                          fontWeight: 600,
                        }}
                      />
                      {idx < steps.length - 1 && (
                        <Typography color="text.disabled" fontSize={18}>→</Typography>
                      )}
                    </React.Fragment>
                  ))
                )}
              </Box>

              <Stack spacing={1.25}>
                {steps.map((step, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Tooltip title={step.label} placement="left">
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          flexShrink: 0,
                          borderRadius: 1,
                          bgcolor: alpha(step.color, 0.12),
                          color: step.color,
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {idx + 1}
                      </Box>
                    </Tooltip>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <EmployeeAutocomplete
                        value={step.value}
                        onChange={step.onChange}
                        color={step.color}
                        placeholder={`${step.label} — เลือกพนักงาน...`}
                      />
                    </Box>
                  </Box>
                ))}
              </Stack>
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ minWidth: 140, borderRadius: 2 }}
            >
              {saving ? 'กำลังบันทึก...' : 'บันทึกการตั้งค่า'}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  /* ---- Mobile: master-detail navigation ---- */
  if (isMobile) {
    return (
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{title}</Typography>
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', minHeight: 400 }}>
          {!mobileShowDetail ? (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <HowToRegOutlinedIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
                  ประเภทการอนุมัติ ({APPROVAL_CATEGORIES.length})
                </Typography>
              </Box>
              {APPROVAL_CATEGORIES.map(renderCategoryItem)}
            </Box>
          ) : (
            renderRightPanel()
          )}
        </Paper>
      </Box>
    );
  }

  /* ---- Desktop: side-by-side ---- */
  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{title}</Typography>
      <Paper
        variant="outlined"
        sx={{ display: 'flex', borderRadius: 2, overflow: 'hidden', height: 'calc(100vh - 180px)', minHeight: 400 }}
      >
        {renderLeftPanel()}
        {renderRightPanel()}
      </Paper>
    </Box>
  );
}
